// FLAGS: identity
// Runs the Google OAuth code flow and registers the chosen calendar for the signed-in expert.
import {
  Button,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useGoogleLogin } from "@react-oauth/google";
import authenticationService from "@src/services/authentication.service";
import { useCalendarQuery } from "@src/queries/calendar";
import { timeSnackbar } from "@src/constants";
import { DialogActionButtons, DialogHeader } from "./DialogToolkit";
import { withSnackbar, WithSnackbarProps } from "./SnackBarComponent";

interface Calendar {
  calendarId: string;
  summary: string;
}

interface RegisterCalendarDialogProps extends WithSnackbarProps {
  open: boolean;
  onClose: () => void;
}

const RegisterCalendarDialog: React.FC<RegisterCalendarDialogProps> = ({
  open,
  onClose,
  snackbarShowMessage,
}) => {
  const { t } = useTranslation();
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [notRegistered, setNotRegistered] = useState(false);
  const { refetch, calendarData, registerCalendar, isCalendarSubmitting } =
    useCalendarQuery();

  useEffect(() => {
    if (calendarData?.notRegistered) {
      setNotRegistered(true);
    } else {
      setCalendars(calendarData);
    }
  }, [calendarData]);

  // FLAGS: identity
  // Registers delegated Google Calendar access for the signed-in expert. It passes the literal
  // "Google" where grantCalendarAccess expects the OAuth authorization code, so the code returned by
  // useGoogleLogin is discarded.
  const onSuccess = async () => {
    await authenticationService.grantCalendarAccess("Google");
    snackbarShowMessage("Success", "success");

    setTimeout(() => {
      setNotRegistered(false);
      refetch();
    }, timeSnackbar);
  };

  const handleSuccess = () => {
    onSuccess().catch((error) =>
      snackbarShowMessage(error?.response?.data || "Error", "error"),
    );
  };

  const onFailure = (res) => {
    if (res?.error !== "popup_closed_by_user") {
      snackbarShowMessage(res.response?.responseText || "Error", "error");
    }
  };

  const login = useGoogleLogin({
    onSuccess: handleSuccess,
    scope: "https://www.googleapis.com/auth/calendar",
    flow: "auth-code",
    onError: onFailure,
  });

  const formik = useFormik({
    initialValues: {
      calendarId: calendars?.length === 1 ? calendars[0]?.calendarId : "",
    },
    validationSchema: Yup.object({
      calendarId: Yup.string().required(t("RegisterCalendar.Error")),
    }),
    onSubmit: async (values) => {
      try {
        formik.setSubmitting(true);
        await registerCalendar(values.calendarId);
        snackbarShowMessage(t("RegisterCalendar.Success"), "success");
        formik.setSubmitting(false);
        onClose();
      } catch (e) {
        if (e.response?.errors) {
          Object.entries(e.response.data.errors).forEach(([key, value]) => {
            snackbarShowMessage(`${key}: ${value}`, "error");
          });
        } else {
          snackbarShowMessage(t("RegisterCalendar.Error"), "error");
        }
        formik.setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader
        iconLigature="calendar_today"
        title={t("Calendar.Add")}
        outlined
      />

      {notRegistered && (
        <Button
          className="bg-secondary text-white rounded-full px-8 py-2 mt-4"
          variant="contained"
          color="secondary"
          onClick={() => login()}
        />
      )}

      {calendars?.length > 0 && (
        <form
          onSubmit={formik.handleSubmit}
          className="space-y-4 p-6"
          noValidate
        >
          <FormControl variant="standard" className="w-full" required>
            <InputLabel id="calendarId-label">
              {t("Common.Calendar")}
            </InputLabel>
            <Select
              variant="standard"
              name="calendarId"
              labelId="calendarId-label"
              id="calendarId"
              error={!!formik.errors.calendarId}
              onChange={formik.handleChange}
              value={formik.values.calendarId}
              className="w-full"
            >
              {calendars.map(
                (calendar) =>
                  calendar && (
                    <MenuItem
                      key={calendar.calendarId}
                      value={calendar.calendarId}
                    >
                      {calendar.summary}
                    </MenuItem>
                  ),
              )}
            </Select>
            {formik.errors.calendarId && (
              <div className="text-red-500 text-sm mt-1">
                {formik.errors.calendarId}
              </div>
            )}
          </FormControl>
        </form>
      )}
      <DialogActionButtons
        onCancel={onClose}
        onSubmit={formik.submitForm}
        submitting={isCalendarSubmitting}
      />
    </Dialog>
  );
};

export default withSnackbar(RegisterCalendarDialog);
